<template>
	<div class="dashboard-group">
		<!-- Header -->
		<div class="flex items-center justify-between">
			<div>
				<Toggle
				v-if="isSuperAdmin"
				:checked="includeOtherRoles"
				:onInput="() => includeOtherRoles = !includeOtherRoles"></Toggle>
				<span v-if="isSuperAdmin" class="ml-2">Inclure les admins/responsables clients</span>
			</div>
			<button type="button" class="mb-4 twn-button" @click="ExportReport" v-bind:disabled="isLoading">
				<span v-if="!isLoading">
					Exporter le rapport
				</span>
				<span v-else>
					Chargement...
				</span>
			</button>
		</div>

		<!-- Dashboard -->
		<p v-if="isLoading || !trackingData">
			Chargement...
		</p>
		<div v-else>
			<!-- Users count and state data -->
			<CategoryBlock title="Utilisateurs">
				<!-- KPIs -->
				<div class="inline-block w-2/3 align-top pr-8">
					<div class="dashboard-group-kpis">
						<KPIBlock
						title="Utilisateurs actifs"
						help="Utilisateurs ayant choisi leur mot de passe"
						:number="trackingData.usersDashboardData.active"
						color="green" />
						<KPIBlock
						title="Utilisateurs inactifs"
						help="Utilisateurs ayant reçu leur mail d'invitation, mais qui n'ont pas encore choisi leur mot de passe"
						:number="trackingData.usersDashboardData.inactive"
						color="red" />
						<KPIBlock
						title="Complétion globale"
						:percentage="trackingData.usersDashboardData.hasFinished"
						color="blue-dark" />
					</div>

					<div class="dashboard-group-kpis">
						<KPIBlock
						title="Utilisateurs inscrits"
						help="Nombre total d'utilisateurs tous statuts confondus"
						:number="trackingData.usersDashboardData.total" />
						<KPIBlock
						title="Utilisateurs en attente"
						help="Utilisateurs qui n'ont pas encore reçu leur mail d'invitation"
						:number="trackingData.usersDashboardData.pending" />
						<KPIBlock
						title="Utilisateurs suspendus"
						:number="trackingData.usersDashboardData.disabled" />
					</div>
				</div>

				<!-- Users distribution -->
				<SubCategoryBlock class="inline-block w-1/3 align-top" title="Répartition par métier">
					<PieChart :data="trackingData.usersDashboardData.percentByJob" />
				</SubCategoryBlock>

				<!-- Users mailings data -->
				<SubCategoryBlock class="mt-8" title="Mails de relance envoyés">
					<div class="dashboard-group-kpis pt-4 pb-5">
						<KPIBlock title="J+1 jour session ratée" :percentage="trackingData.userMailingDashboardData.noLogin" color="gray-lighter" text-color="dashboard-blue-dark" />
						<KPIBlock title="J+1 jour formation non-terminée" :percentage="trackingData.userMailingDashboardData.notFinishedAfterOneDay" color="gray-lighter" text-color="dashboard-blue-dark" />
						<KPIBlock title="J+30 jours formation non-terminée" :percentage="trackingData.userMailingDashboardData.notFinishedAfterOneMonth" color="gray-lighter" text-color="dashboard-blue-dark" />
						<div class="flex-1"></div>
					</div>

					<div class="dashboard-group-finish-by-job">
						<div
						:class="{ 'flex-1': (index > 0)}"
						:style="inlineBarChartStyle(index, trackingData.userMailingDashboardData.byJob)"
						v-for="(jobData, jobSlug, index) in trackingData.userMailingDashboardData.byJob"
						:key="jobSlug">
							<BarChart
							:data="jobData"
							:high-value="1"
							:serie-name="jobSlug"
							:horizontal-bars="true"
							:show-legend="false"
							:labels="(index == 0 ? reminderMailLabels : null)"
							format="percentage" />
							<p class="ct-label">{{jobs[jobSlug]}}</p>
						</div>
					</div>
				</SubCategoryBlock>
			</CategoryBlock>

			<!-- Users activity data -->
			<CategoryBlock title="Données de connexion">
				<!-- Active users -->
				<SubCategoryBlock class="dashboard-group-active-user mb-8" title="Nombre d'utilisateurs actifs par">
					<div class="inline-block ml-6">
						<span class="mr-2">Mois</span>
						<Toggle
						:checked="!showUserActivityByMonth"
						:onInput="() => showUserActivityByMonth = !showUserActivityByMonth"></Toggle>
						<span class="ml-2">Années</span>
					</div>

					<LineChart :data="trackingData.userActivityDashboardData.activeCount[(showUserActivityByMonth ? 'months' : 'years')]" />
				</SubCategoryBlock>

				<!-- Active users times -->
				<div class="flex">
					<SubCategoryBlock class="flex-1 mr-4" title="Durée totale de connexion">
						<BarPieChart
						class="mt-12"
						:data="trackingData.userActivityDashboardData.activeTime.sum" />
					</SubCategoryBlock>

					<SubCategoryBlock class="flex-1 ml-4" title="Durée moyenne de connexion">
						<BarChart
						:data="trackingData.userActivityDashboardData.activeTime.average"
						format="time" />
					</SubCategoryBlock>
				</div>
			</CategoryBlock>

			<!-- Users progression data -->
			<CategoryBlock title="Données pédagogiques">
				<!-- Users count by status -->
				<SubCategoryBlock class="mb-8" title="Progression des utilisateurs selon le métier">
					<BarChart
					:data="trackingData.userProgressionDashboardData.userCounts"
					:enable-tooltip="true"
					:horizontal-bars="true"
					:stack-bars="true"
					:show-stack-sum="true"
					:labels="userCountsLabels" />
				</SubCategoryBlock>

				<!-- Users time to finish distribution -->
				<SubCategoryBlock title="Taux de complétion du niveau 2 dans la durée">
					<div class="dashboard-group-kpis pt-4 pb-5">
						<KPIBlock title="J+1 mois" :percentage="trackingData.userProgressionDashboardData.hasFinishedIn.oneMonth" color="gray-lighter" text-color="dashboard-blue-dark" />
						<KPIBlock title="J+3 mois" :percentage="trackingData.userProgressionDashboardData.hasFinishedIn.threeMonth" color="gray-lighter" text-color="dashboard-blue-dark" />
						<KPIBlock title="J+1 an" :percentage="trackingData.userProgressionDashboardData.hasFinishedIn.oneYear" color="gray-lighter" text-color="dashboard-blue-dark" />
						<div class="flex-1"></div>
						<div class="flex-1"></div>
						<div class="flex-1"></div>
					</div>

					<div class="dashboard-group-finish-by-job">
						<div
						:class="{ 'flex-1': (index > 0)}"
						:style="inlineBarChartStyle(index, trackingData.userProgressionDashboardData.hasFinishedIn.byJob)"
						v-for="(jobData, jobSlug, index) in trackingData.userProgressionDashboardData.hasFinishedIn.byJob"
						:key="jobSlug">
							<BarChart
							:data="jobData"
							:high-value="1"
							:serie-name="jobSlug"
							:horizontal-bars="true"
							:show-legend="false"
							:labels="(index == 0 ? hasFinishedInLabels : null)"
							format="percentage" />
							<p class="ct-label">{{jobs[jobSlug]}}</p>
						</div>
					</div>
				</SubCategoryBlock>
			</CategoryBlock>

		</div>
	</div>
</template>

<script>
	import trackingReport from '@/utils/trackingReport'

	import userFieldsValues from "@/constants/userFieldsValues"
	import trackingReportTemplate from "@/constants/groupTrackingReportTemplate"

	import Toggle from '@/components/Toggle'

	import CategoryBlock from "./block/Category"
	import SubCategoryBlock from "./block/SubCategory"
	import KPIBlock from "./block/KPI"
	import PieChart from "./chart/Pie"
	import LineChart from "./chart/Line"
	import BarChart from "./chart/Bar"
	import BarPieChart from "./chart/BarPie"

	export default {
		name: 'MainDashboard',
		components: {
			Toggle,
			CategoryBlock,
			SubCategoryBlock,
			KPIBlock,
			PieChart,
			LineChart,
			BarChart,
			BarPieChart
		},
		props: {
			group: {
				type: Object,
				required: true
			}
		},
		data() {
			return {
				isLoading: false,
				trackingData: null,
				includeOtherRoles: false,
				showUserActivityByMonth: true,
				jobs: userFieldsValues.job.reduce((dict, job) => {
					dict[job.value] = (job.labelShort || job.label)

					return dict
				}, {})
			}
		},
		computed: {
			isSuperAdmin() {
				const userInfo = this.$store.state.Auth.userInfo

				return (userInfo && userInfo.role == 'superadmin')
			},
			userCountsLabels() {
				const keys = Object.keys(this.trackingData.userProgressionDashboardData.userCounts)

				const labels = {
					hasNotStarted: 'utilisateurs inactifs',
					isInProgress: 'utilisateurs en cours',
					hasDoneLevel1: 'niveau 01 terminé',
					hasDoneLevel2: 'niveau 02 terminé'
				}

				return keys.map((key) => (labels[key] || key))
			},
			hasFinishedInLabels() {
				return ['J+1 mois', 'J+3 mois', 'J+1 an']
			},
			reminderMailLabels() {
				return ['J+1 jour session ratée', 'J+1 jour formation non-terminée', 'J+30 jours formation non-terminée']
			}
		},
		watch: {
			group: {
				async handler(group) {
					this.isLoading = true

					// Load tracking data
					const payload = { id: group.id }

					if (this.includeOtherRoles) {
						payload.roles = ['user', 'superadmin', 'customer_manager']
					}

					this.trackingData = await trackingReport.loadData(this.$store, trackingReportTemplate, payload)

					this.isLoading = false
				},
				immediate: true
			},
			async includeOtherRoles(includeOtherRoles) {
				this.isLoading = true

				// Load tracking data
				const payload = { id: this.group.id }

				if (includeOtherRoles) {
					payload.roles = ['user', 'superadmin', 'customer_manager']
				}

				this.trackingData = await trackingReport.loadData(this.$store, trackingReportTemplate, payload)

				this.isLoading = false
			}
		},
		methods: {
			inlineBarChartStyle(index, data) {
				if (index > 0)
					return ''

				return 'width: calc(' + (100 / Object.keys(data).length) + '% + 100px);'
			},
			async ExportReport() {
				this.isLoading = true
				
				try {
					const groupName = (this.group.identifier ? this.group.identifier : this.group.name).substring(0, 7).trim().replace(' ', '_')
					await trackingReport.export('Promotion_' + groupName, this.$store, trackingReportTemplate, null, this.trackingData)
				} catch (err) {
					// console.error(err)
				}

				this.isLoading = false
			}
		}
	}
</script>

<style lang="scss">
	.dashboard-group {
		@apply max-w-screen-lg mx-auto;

		&-kpis {
			@apply flex flex-wrap justify-between;

			&:first-child {
				@apply mb-8;
			}
		}

		.dashboard-category {
			@apply w-full mb-5;

			.dashboard-kpi {
				@apply flex-1 mx-4;

				&:first-child {
					@apply ml-0;
				}

				&:last-child {
					@apply mr-0;
				}
			}
		}

		&-active-user {
			& > p {
				display: inline-block;
			}
		}

		&-finish-by-job {
			@apply flex;
			padding-left: 100px;

			.dashboard-chart {
				@apply pr-8 mb-3;
			}

			& > div:first-child {
				margin-left: -100px;

				p.ct-label {
					margin-left: 100px;
				}
			}

			p.ct-label {
				@apply text-dashboard-blue-dark;

				font-style: italic;
			}
		}
	}
</style>